import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Renders status, queue position, elapsed time, and the final report (when
 * done) for a single analysis job. Polls via `useJob`.
 */
import { useEffect, useState } from 'react';
import { Loader2, X, CheckCircle2, AlertCircle, Download, FileText } from 'lucide-react';
import { useCancelJob, useJob } from '@/hooks/queries';
import { SchemaReportRenderer } from './SchemaReportRenderer';
function humanizeKey(key) {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
const NOISE_KEYS = new Set([
    'raw',
    'choices',
    'tool_calls',
    'usage',
    'media_info',
    'object',
    'model',
    'created',
    'finish_reason',
    'index',
    'role',
    'id',
    'video_id',
    'uuid',
    'post_process_error',
    'lvs_response',
    'aggregation_latency',
    'summary_latency',
    'query_processing_time',
    'total_chunks_processed',
    'aggregation_tokens',
    'summary_tokens',
    'summary_requests',
    'start_offset',
    'end_offset',
]);
function tryParseJson(value) {
    if (typeof value !== 'string')
        return value;
    const t = value.trim();
    if (!((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))))
        return value;
    try {
        return JSON.parse(t);
    }
    catch {
        return value;
    }
}
function cleanReport(value) {
    if (Array.isArray(value))
        return value.map(cleanReport);
    if (value && typeof value === 'object') {
        let working = value;
        // Unwrap LVS envelope hidden inside a JSON-string `summary`.
        const sum = working['summary'];
        if (typeof sum === 'string') {
            const u = tryParseJson(sum);
            if (u && typeof u === 'object' && !Array.isArray(u)) {
                const env = u;
                const choices = env['choices'];
                if (Array.isArray(choices) && choices[0] && typeof choices[0] === 'object') {
                    const msg = choices[0]['message'];
                    const content = msg && typeof msg === 'object' ? msg['content'] : null;
                    const inner = tryParseJson(content);
                    if (inner && typeof inner === 'object')
                        working = inner;
                }
                else {
                    working = env;
                }
            }
        }
        const out = {};
        for (const [k, v] of Object.entries(working)) {
            if (NOISE_KEYS.has(k.toLowerCase()))
                continue;
            out[k] = cleanReport(v);
        }
        return out;
    }
    return value;
}
function reportToMarkdown(report, depth = 0) {
    if (report === null || report === undefined || report === '')
        return '_—_';
    if (typeof report === 'string') {
        const t = report.trim();
        if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
            try {
                return reportToMarkdown(JSON.parse(t), depth);
            }
            catch { /* fall through */ }
        }
        return report;
    }
    if (typeof report === 'number' || typeof report === 'boolean')
        return String(report);
    if (Array.isArray(report)) {
        if (report.length === 0)
            return '_(none)_';
        if (report.every((v) => typeof v === 'string' || typeof v === 'number')) {
            return report.map((v) => `- ${v}`).join('\n');
        }
        return report.map((v) => reportToMarkdown(v, depth)).join('\n\n---\n\n');
    }
    if (typeof report === 'object') {
        const entries = Object.entries(report);
        return entries
            .map(([k, v]) => {
            const label = humanizeKey(k);
            const heading = '#'.repeat(Math.min(6, depth + 2));
            const body = reportToMarkdown(v, depth + 1);
            if (typeof v === 'object' && v !== null) {
                return `${heading} ${label}\n\n${body}`;
            }
            return `**${label}:** ${body}`;
        })
            .join('\n\n');
    }
    return String(report);
}
function triggerDownload(filename, mime, content) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}
const STATUS_COLOR = {
    queued: '#8c8c8c',
    running: '#1890ff',
    done: '#52c41a',
    failed: '#ff4d4f',
    cancelled: '#faad14',
};
function fmtElapsed(startedAt) {
    if (!startedAt)
        return '—';
    const s = Math.max(0, Math.floor((Date.now() / 1000) - startedAt));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}m ${ss.toString().padStart(2, '0')}s`;
}
export function JobProgressCard({ jobId, onSeek, onClear }) {
    const { data: job, isLoading, error } = useJob(jobId);
    const cancel = useCancelJob();
    const [, setTick] = useState(0);
    // Re-render every second while running so the elapsed clock advances.
    useEffect(() => {
        if (!job || job.status === 'done' || job.status === 'failed' || job.status === 'cancelled')
            return;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [job?.status]);
    if (isLoading) {
        return (_jsxs("div", { className: "card", style: { background: 'var(--ey-darker)' }, children: [_jsx(Loader2, { className: "spin", size: 14 }), " Loading job ", jobId, "\u2026"] }));
    }
    if (error || !job) {
        return (_jsxs("div", { className: "card", style: { background: 'var(--ey-darker)', color: '#ff4d4f' }, children: [_jsx(AlertCircle, { size: 14 }), " Failed to load job ", jobId, ": ", error?.message] }));
    }
    const color = STATUS_COLOR[job.status];
    const isTerminal = job.status === 'done' || job.status === 'failed' || job.status === 'cancelled';
    return (_jsxs("div", { className: "card", style: { background: 'var(--ey-darker)', display: 'grid', gap: 10 }, children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [job.status === 'done' && _jsx(CheckCircle2, { size: 16, color: color }), (job.status === 'queued' || job.status === 'running') && _jsx(Loader2, { className: "spin", size: 14, color: color }), (job.status === 'failed' || job.status === 'cancelled') && _jsx(AlertCircle, { size: 16, color: color }), _jsx("strong", { style: { color }, children: job.status.toUpperCase() }), _jsxs("span", { style: { color: 'var(--ey-muted)', fontSize: 12 }, children: [job.persona ? `${job.persona} lens` : 'Default lens', " \u00B7 ", job.depth, " depth"] })] }), _jsxs("div", { style: { display: 'flex', gap: 6 }, children: [job.status === 'done' && job.result?.report && (_jsxs(_Fragment, { children: [_jsxs("button", { className: "btn btn-sm", onClick: () => {
                                            const ts = new Date().toISOString().replace(/[:.]/g, '-');
                                            const slug = (job.persona || 'report').replace(/\s+/g, '_').toLowerCase();
                                            triggerDownload(`eyva-${slug}-${ts}.json`, 'application/json', JSON.stringify({
                                                job_id: job.job_id,
                                                persona: job.persona,
                                                depth: job.depth,
                                                video_id: job.video_id,
                                                finished_at: job.finished_at,
                                                report: cleanReport(job.result?.report),
                                                extra: job.extra,
                                            }, null, 2));
                                        }, title: "Download report as JSON", children: [_jsx(Download, { size: 12 }), " JSON"] }), _jsxs("button", { className: "btn btn-sm", onClick: () => {
                                            const ts = new Date().toISOString().replace(/[:.]/g, '-');
                                            const slug = (job.persona || 'report').replace(/\s+/g, '_').toLowerCase();
                                            const header = `# EY Video Analyzer Analysis Report\n\n` +
                                                `- **Persona:** ${job.persona ?? '—'}\n` +
                                                `- **Depth:** ${job.depth ?? '—'}\n` +
                                                `- **Source:** ${job.video_id ?? '—'}\n` +
                                                `- **Job ID:** ${job.job_id}\n` +
                                                `- **Generated:** ${new Date().toLocaleString()}\n\n---\n\n`;
                                            triggerDownload(`eyva-${slug}-${ts}.md`, 'text/markdown;charset=utf-8', header + reportToMarkdown(cleanReport(job.result?.report)) + '\n');
                                        }, title: "Download report as Markdown", children: [_jsx(FileText, { size: 12 }), " Markdown"] })] })), !isTerminal && job.status === 'queued' && (_jsxs("button", { className: "btn btn-sm btn-secondary", onClick: () => cancel.mutate(job.job_id), disabled: cancel.isPending, title: "Cancel job", children: [_jsx(X, { size: 12 }), " Cancel"] })), isTerminal && onClear && (_jsx("button", { className: "btn btn-sm", onClick: onClear, title: "Dismiss", children: _jsx(X, { size: 12 }) }))] })] }), _jsxs("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 12 }, children: [_jsxs("div", { children: [_jsx("div", { style: { color: 'var(--ey-muted)' }, children: "Queue position" }), _jsx("div", { style: { fontWeight: 600 }, children: job.position || (job.status === 'queued' ? '?' : '—') })] }), _jsxs("div", { children: [_jsx("div", { style: { color: 'var(--ey-muted)' }, children: "Elapsed" }), _jsx("div", { style: { fontWeight: 600 }, children: fmtElapsed(job.started_at) })] }), _jsxs("div", { title: "Number of timeline segments / findings detected in the video", children: [_jsx("div", { style: { color: 'var(--ey-muted)' }, children: "Detected events" }), _jsx("div", { style: { fontWeight: 600 }, children: (() => {
                                    const report = job.result?.report;
                                    if (!report)
                                        return job.status === 'done' ? '0' : '—';
                                    const candidates = [
                                        'events',
                                        'suspicious_behavior',
                                        'key_events',
                                        'hazards',
                                        'unsafe_behaviors',
                                        'policy_observations',
                                        'violations',
                                    ];
                                    for (const k of candidates) {
                                        const v = report[k];
                                        if (Array.isArray(v))
                                            return v.length;
                                    }
                                    return job.status === 'done' ? '1' : '—';
                                })() })] })] }), job.status === 'failed' && job.error && (_jsx("div", { style: { color: '#ff4d4f', fontSize: 13, whiteSpace: 'pre-wrap' }, children: job.error })), job.status === 'done' && job.result?.report && (_jsx(SchemaReportRenderer, { data: job.result.report, onSeek: onSeek }))] }));
}
